import type { UseQueryResult } from '@tanstack/react-query';
import type {
  Api,
  BookingResponse,
  GuestProfileResponse,
  InvoiceResponse,
  ObjectApartmentSummary,
} from '@/shared/api';
import { BookingReassignmentHistory } from '../reassignment';
import { BookingBillingWorkspace } from './BookingBillingWorkspace';
import type { BookingDetailTab } from './bookingDetailTabModel';
import { BookingGuestTab } from './BookingGuestTab';
import type { InvoiceStepState } from './bookingInvoiceModel';
import { BookingOverview } from './BookingOverview';
import { ChannexSyncCollapsible } from './ChannexSyncCollapsible';
import type { GuestState } from './useBookingWorkspace';

interface Props {
  active: BookingDetailTab;
  api: Api;
  booking: BookingResponse;
  guest: UseQueryResult<GuestProfileResponse>;
  guestState: GuestState;
  guestName: string;
  /** `null`: noch nicht da oder nicht geladen (`invoicesFailed`). */
  invoices: InvoiceResponse[] | null;
  invoicesFailed: boolean;
  apartments: ObjectApartmentSummary[];
  onboardingComplete: boolean | null;
  invoiceState: InvoiceStepState | null;
  onChangeTab: (tab: BookingDetailTab) => void;
}

/** Inhalt des gewählten Reiters; nur für Verwalter (Eigentümer sehen keine Reiter). */
export function BookingTabContent(props: Props) {
  const { active, booking } = props;
  if (active === 'overview') {
    return (
      <BookingOverview
        booking={booking}
        guest={props.guest.data}
        guestState={props.guestState}
        guestName={props.guestName}
        invoices={props.invoices}
        invoicesFailed={props.invoicesFailed}
        onboardingComplete={props.onboardingComplete}
        invoiceState={props.invoiceState}
        onChangeTab={props.onChangeTab}
      />
    );
  }
  if (active === 'billing') {
    return (
      <BookingBillingWorkspace
        booking={booking}
        invoices={props.invoices}
        guest={props.guest.data}
      />
    );
  }
  if (active === 'history') {
    const names = Object.fromEntries(
      props.apartments.map((a) => [a.id, a.adminDisplayName ?? a.name]),
    );
    return (
      <div className="flex flex-col gap-4">
        <BookingReassignmentHistory bookingId={booking.id} propertyNames={names} />
        <ChannexSyncCollapsible booking={booking} />
      </div>
    );
  }
  return (
    <BookingGuestTab
      api={props.api}
      booking={booking}
      guest={props.guest}
      guestState={props.guestState}
      guestName={props.guestName}
    />
  );
}
